/**
 * Serializes writes so that a slow earlier save can never land after a faster
 * later one.
 *
 * Each key (one character, or one session) gets its own chain of promises. A
 * new write is appended to that key's chain and only starts once the previous
 * write for the same key has settled. Different keys still run in parallel.
 *
 * This does NOT replace the atomic write in electron/fileStore.cjs. That
 * guarantees a single write is never half-finished; this guarantees the ORDER
 * of several writes.
 */
export class WriteQueue {
  private readonly chains = new Map<string, Promise<unknown>>();

  /** Runs `task` after every earlier task for the same key has finished. */
  run<T>(key: string, task: () => Promise<T>): Promise<T> {
    const previous = this.chains.get(key) ?? Promise.resolve();
    // Swallow the earlier error here so one failed write does not poison the
    // chain; the original caller still receives its own rejection.
    const next = previous.then(task, task);
    this.chains.set(
      key,
      next.catch(() => undefined),
    );
    return next;
  }

  /** Resolves once every queued write has settled. Used by tests and shutdown. */
  async drain(): Promise<void> {
    await Promise.all([...this.chains.values()].map((p) => p.catch(() => undefined)));
  }

  pending(): number {
    return this.chains.size;
  }
}
